// Planos (/plans) — página filha de Empresas, só para super admin.
// Mockup: "Página filha — Planos (PlansManager · acesso pelo botão Planos)".
//
// Substitui a aba Planos de Configurações. O editor fica ao lado dos cards e
// não em modal: editar plano é comparar com os outros, e o mockup mantém a
// grade visível durante a edição.
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Tooltip from "@material-ui/core/Tooltip";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

import MainContainer from "../../components/MainContainer";
import ForbiddenPage from "../../components/ForbiddenPage";
import AerosConfirmModal from "../../components/AerosConfirmModal";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import useCompanies from "../../hooks/useCompanies";
import useCampaignPageStyles from "../Campaigns/campaignPageStyles";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import usePlanPageStyles from "./planPageStyles";
import { parseAmount } from "../Companies/companyRules";
import { periodoLabel } from "./planRules";
import {
  precosExibiveis,
  normalizeAmountInput,
} from "../../components/PlansManager/planPriceRules";

// Recursos do plano, na ordem do mockup. A chave é a coluna de `Plans`.
const RECURSOS = [
  { key: "useWhatsapp", label: "aeros.plans.feature.whatsapp" },
  { key: "useWhatsappOfficial", label: "aeros.plans.feature.whatsappOfficial" },
  { key: "useFacebook", label: "aeros.plans.feature.facebook" },
  { key: "useInstagram", label: "aeros.plans.feature.instagram" },
  { key: "useCampaigns", label: "aeros.plans.feature.campaigns" },
  { key: "useSchedules", label: "aeros.plans.feature.schedules" },
  { key: "useInternalChat", label: "aeros.plans.feature.internalChat" },
  { key: "useExternalApi", label: "aeros.plans.feature.externalApi" },
  { key: "useKanban", label: "aeros.plans.feature.kanban" },
  { key: "useOpenAi", label: "aeros.plans.feature.openAi" },
  { key: "useIntegrations", label: "aeros.plans.feature.integrations" },
  { key: "wavoip", label: "aeros.plans.feature.wavoip" },
];

const PLANO_VAZIO = {
  name: "",
  users: 0,
  connections: 0,
  queues: 0,
  amount: "0",
  amountYearly: "",
  isPublic: true,
  trial: false,
  trialDays: 0,
  ...RECURSOS.reduce((acc, r) => ({ ...acc, [r.key]: false }), {}),
};

const Plans = () => {
  const c = useCampaignPageStyles();
  const m = useAerosModalStyles();
  const classes = usePlanPageStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { list, save, update, remove } = usePlans();
  const { list: listCompanies } = useCompanies();

  const [planos, setPlanos] = useState([]);
  const [usoPorPlano, setUsoPorPlano] = useState({});
  const [record, setRecord] = useState({ ...PLANO_VAZIO });
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [excluindo, setExcluindo] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const lista = await list();
      setPlanos(Array.isArray(lista) ? lista : []);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);

    // Quantas empresas usam cada plano — o rodapé do card. Falha aqui não
    // impede editar planos, então não vira erro na tela.
    try {
      const companies = await listCompanies();
      const contagem = {};
      (Array.isArray(companies) ? companies : []).forEach((company) => {
        if (!company.planId) return;
        contagem[company.planId] = (contagem[company.planId] || 0) + 1;
      });
      setUsoPorPlano(contagem);
    } catch (err) {
      setUsoPorPlano({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const editando = record.id !== undefined;

  const ordenados = useMemo(
    () => [...planos].sort((a, b) => parseAmount(a.amount) - parseAmount(b.amount)),
    [planos]
  );

  const handleSelecionar = (plano) => {
    const flag = (v) => v === true;
    setRecord({
      ...PLANO_VAZIO,
      ...plano,
      amount: String(plano.amount ?? "0"),
      amountYearly: plano.amountYearly ? String(plano.amountYearly) : "",
      isPublic: plano.isPublic !== false,
      trial: flag(plano.trial),
      trialDays: plano.trialDays || 0,
      ...RECURSOS.reduce(
        (acc, r) => ({ ...acc, [r.key]: flag(plano[r.key]) }),
        {}
      ),
    });
  };

  const handleSalvar = async () => {
    if (!record.name.trim()) {
      toast.error(i18n.t("aeros.plans.nameRequired"));
      return;
    }
    setSalvando(true);
    try {
      // Vazio vira null: Postgres recusa "" em DECIMAL.
      const payload = {
        ...record,
        amountYearly: normalizeAmountInput(record.amountYearly),
      };
      if (editando) {
        await update(payload);
      } else {
        await save(payload);
      }
      toast.success(i18n.t("aeros.plans.saved"));
      setRecord({ ...PLANO_VAZIO });
      await carregar();
    } catch (err) {
      toastError(err);
    }
    setSalvando(false);
  };

  const handleExcluir = async () => {
    if (!excluindo) return;
    try {
      await remove(excluindo.id);
      toast.success(i18n.t("aeros.plans.deleted"));
      if (record.id === excluindo.id) setRecord({ ...PLANO_VAZIO });
      await carregar();
    } catch (err) {
      toastError(err);
    }
    setExcluindo(null);
  };

  // O valor sai do evento AQUI, não dentro do updater: o React 16 recicla o
  // SyntheticEvent ao fim do handler, e o updater funcional pode ser executado
  // depois, já no render — aí `e.target` é null e a tela quebra.
  const campo = (nome) => (e) => {
    const valor = e.target.value;
    setRecord((prev) => ({ ...prev, [nome]: valor }));
  };

  if (!user?.super) {
    return <ForbiddenPage />;
  }

  const renderCard = (plano) => {
    const emUso = usoPorPlano[plano.id];
    return (
      <div
        key={plano.id}
        className={`${classes.planCard} ${
          record.id === plano.id ? classes.planCardActive : ""
        }`}
      >
        <div className={classes.planHead}>
          <div className={classes.planName} title={plano.name}>
            {plano.name}
          </div>
          {plano.isPublic !== false && (
            <span className={`${classes.tag} ${classes.tagPublic}`}>
              {i18n.t("aeros.plans.public")}
            </span>
          )}
          {plano.trial && (
            <span className={`${classes.tag} ${classes.tagTrial}`}>
              {i18n.t("aeros.plans.trialDays", { count: plano.trialDays || 0 })}
            </span>
          )}
          <div className={classes.cardActions}>
            <Tooltip title={i18n.t("aeros.plans.edit")}>
              <IconButton
                className={c.rowActionButton}
                aria-label={i18n.t("aeros.plans.edit")}
                onClick={() => handleSelecionar(plano)}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={i18n.t("aeros.plans.delete")}>
              <IconButton
                className={`${c.rowActionButton} ${c.rowActionButtonDanger}`}
                aria-label={i18n.t("aeros.plans.delete")}
                onClick={() => setExcluindo(plano)}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          </div>
        </div>

        <div>
          {/* Só os períodos com valor: plano só anual não mostra "/ mês". */}
          {precosExibiveis(plano).map((p) => (
            <div key={p.period}>
              <span className={classes.price}>{p.label}</span>
              <span className={classes.pricePer}>
                {` ${periodoLabel(p.period)}`}
              </span>
            </div>
          ))}
        </div>

        <div className={classes.limits}>
          <span>
            <b>{plano.users ?? 0}</b> {i18n.t("aeros.plans.users")}
          </span>
          <span>
            <b>{plano.connections ?? 0}</b> {i18n.t("aeros.plans.connections")}
          </span>
          <span>
            <b>{plano.queues ?? 0}</b> {i18n.t("aeros.plans.queues")}
          </span>
        </div>

        <div className={classes.features}>
          {RECURSOS.map((r) => (
            <span
              key={r.key}
              className={`${classes.feature} ${
                plano[r.key] ? "" : classes.featureOff
              }`}
            >
              {i18n.t(r.label)}
            </span>
          ))}
        </div>

        {emUso !== undefined && (
          <div className={classes.usage}>
            {i18n.t(
              emUso === 1 ? "aeros.plans.usageOne" : "aeros.plans.usage",
              { count: emUso }
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <MainContainer>
      <AerosConfirmModal
        open={Boolean(excluindo)}
        onClose={() => setExcluindo(null)}
        onConfirm={handleExcluir}
        icon={<DeleteOutlineIcon />}
        tone="danger"
        title={i18n.t("aeros.plans.confirmTitle", { name: excluindo?.name || "" })}
        description={i18n.t("aeros.plans.confirmMessage", {
          count: usoPorPlano[excluindo?.id] || 0,
        })}
        confirmLabel={i18n.t("aeros.plans.delete")}
      />

      <div className={c.pageHeader}>
        <div className={c.breadcrumbBack}>
          <Button
            className={c.outlineButton}
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push("/companies")}
          >
            {i18n.t("compaies.title")}
          </Button>
          <ChevronRightIcon className={c.breadcrumbSep} />
          <div className={c.pageTitle}>
            {i18n.t("aeros.plans.title")}{" "}
            <span className={c.pageTitleCount}>· {planos.length}</span>
          </div>
        </div>

        <div className={c.headerActions}>
          <Button
            variant="contained"
            color="primary"
            className={c.gradientButton}
            startIcon={<AddIcon />}
            onClick={() => setRecord({ ...PLANO_VAZIO })}
          >
            {i18n.t("aeros.plans.newPlan")}
          </Button>
        </div>
      </div>

      <div className={c.pageBody}>
        <div className={classes.layout}>
          <div className={classes.cardGrid}>
            {loading ? (
              <div className={c.loadingRow}>{i18n.t("aeros.companies.loading")}</div>
            ) : ordenados.length === 0 ? (
              <div className={c.emptyState}>
                <div className={c.emptyTitle}>{i18n.t("aeros.plans.emptyTitle")}</div>
              </div>
            ) : (
              ordenados.map(renderCard)
            )}
          </div>

          <div className={classes.editor}>
            <div className={classes.editorTitle}>
              <EditIcon />
              {editando
                ? `${i18n.t("aeros.plans.editPlan")} · ${record.name}`
                : i18n.t("aeros.plans.newPlan")}
            </div>

            <div className={classes.editorGrid}>
              <div className={classes.span2}>
                <label className={m.fieldLabel}>{i18n.t("aeros.plans.name")}</label>
                <TextField
                  fullWidth
                  variant="outlined"
                  className={`${m.input} ${m.inputSmall}`}
                  value={record.name}
                  onChange={campo("name")}
                />
              </div>
              <div>
                <label className={m.fieldLabel}>
                  {i18n.t("aeros.plans.usersLabel")}
                </label>
                <TextField
                  fullWidth
                  type="number"
                  variant="outlined"
                  className={`${m.input} ${m.inputSmall}`}
                  value={record.users}
                  onChange={campo("users")}
                />
              </div>
              <div>
                <label className={m.fieldLabel}>
                  {i18n.t("aeros.plans.connectionsLabel")}
                </label>
                <TextField
                  fullWidth
                  type="number"
                  variant="outlined"
                  className={`${m.input} ${m.inputSmall}`}
                  value={record.connections}
                  onChange={campo("connections")}
                />
              </div>
              <div>
                <label className={m.fieldLabel}>
                  {i18n.t("aeros.plans.queuesLabel")}
                </label>
                <TextField
                  fullWidth
                  type="number"
                  variant="outlined"
                  className={`${m.input} ${m.inputSmall}`}
                  value={record.queues}
                  onChange={campo("queues")}
                />
              </div>
              <div>
                <label className={m.fieldLabel}>{i18n.t("aeros.plans.amount")}</label>
                <TextField
                  fullWidth
                  variant="outlined"
                  className={`${m.input} ${m.inputSmall}`}
                  value={record.amount}
                  onChange={campo("amount")}
                />
              </div>
              <div>
                <label className={m.fieldLabel}>
                  {i18n.t("plans.form.amountYearly")}
                </label>
                <TextField
                  fullWidth
                  variant="outlined"
                  className={`${m.input} ${m.inputSmall}`}
                  value={record.amountYearly}
                  onChange={campo("amountYearly")}
                  inputProps={{
                    inputMode: "decimal",
                    "aria-label": i18n.t("plans.form.amountYearly"),
                  }}
                />
              </div>
            </div>

            <div className={classes.toggleRow}>
              <div
                className={`${classes.toggleBox} ${
                  record.isPublic ? classes.toggleBoxOn : ""
                }`}
              >
                {i18n.t("aeros.plans.public")}
                <Switch
                  color="primary"
                  size="small"
                  style={{ marginLeft: "auto" }}
                  checked={Boolean(record.isPublic)}
                  onChange={(e) => {
                    const marcado = e.target.checked;
                    setRecord((prev) => ({ ...prev, isPublic: marcado }));
                  }}
                  inputProps={{ "aria-label": i18n.t("aeros.plans.public") }}
                />
              </div>
              <div
                className={`${classes.toggleBox} ${
                  record.trial ? classes.toggleBoxOn : ""
                }`}
              >
                {i18n.t("aeros.plans.trial")}
                <Switch
                  color="primary"
                  size="small"
                  style={{ marginLeft: "auto" }}
                  checked={Boolean(record.trial)}
                  onChange={(e) => {
                    const marcado = e.target.checked;
                    setRecord((prev) => ({ ...prev, trial: marcado }));
                  }}
                  inputProps={{ "aria-label": i18n.t("aeros.plans.trial") }}
                />
              </div>
            </div>

            {record.trial && (
              <div>
                <label className={m.fieldLabel}>
                  {i18n.t("aeros.plans.trialDaysField")}
                </label>
                <TextField
                  fullWidth
                  type="number"
                  variant="outlined"
                  className={`${m.input} ${m.inputSmall}`}
                  value={record.trialDays}
                  onChange={campo("trialDays")}
                />
              </div>
            )}

            <div>
              <div className={classes.featuresLabel}>
                {i18n.t("aeros.plans.featuresTitle")}
              </div>
              <div className={classes.featureGrid}>
                {RECURSOS.map((r) => (
                  <FormControlLabel
                    key={r.key}
                    className={classes.featureCheck}
                    control={
                      <Checkbox
                        color="primary"
                        size="small"
                        checked={Boolean(record[r.key])}
                        onChange={(e) => {
                          const marcado = e.target.checked;
                          setRecord((prev) => ({ ...prev, [r.key]: marcado }));
                        }}
                      />
                    }
                    label={i18n.t(r.label)}
                  />
                ))}
              </div>
            </div>

            <div className={classes.editorActions}>
              <Button
                className={`${m.cancelButton} ${classes.editorButton}`}
                onClick={() => setRecord({ ...PLANO_VAZIO })}
              >
                {i18n.t("aeros.plans.clear")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                className={`${c.gradientButton} ${classes.editorButton}`}
                onClick={handleSalvar}
                disabled={salvando}
              >
                {i18n.t("aeros.plans.save")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </MainContainer>
  );
};

export default Plans;
